// src/components/padrekko/qc/ReleaseCaseModal.jsx
//
// REM-576 — the producer releases the case to the owner, and (from the
// hand-off button at dh_review) hands it off in the same act.
//
// RELEASE, THEN STAGE. Two requests, because they are two server acts: the
// release is what lets the owner see the case (migration 134's view); the
// stage move is whose turn it is. In that order, so the owner is never handed
// a case they cannot open (D5: the server allows either alone).
//
// A RETRY IS SAFE. The server's "already released" 409 counts as done, so the
// stage move still runs. If the release succeeds and the stage move fails,
// the dialog stays open with the server's words and offers "Retry hand-off",
// which does not release again: the case is released but still at dh_review,
// readable by the owner and not yet theirs to write.
//
// Without `handOff` (the stall banner, a case already past dh_review) it is
// the release alone.

import { useState } from "react";
import { Loader2, X as XIcon, PackageCheck } from "lucide-react";
import { useQcStore } from "../../../stores/useQcStore";
import { toast } from "../../../stores/useToastStore";
import HandoffWarningsModal from "./HandoffWarningsModal";
import ActionError from "./ActionError";

export default function ReleaseCaseModal({ isOpen, onClose, ownerName, handOff = false, nextStage = null, nextStageLabel = null }) {
  const caseInfo             = useQcStore((s) => s.caseInfo);
  const releaseCase          = useQcStore((s) => s.releaseCase);
  const advanceStage         = useQcStore((s) => s.advanceStage);
  const loadCase             = useQcStore((s) => s.loadCase);
  const releaseInflight      = useQcStore((s) => s.releaseInflight);
  const advanceStageInflight = useQcStore((s) => s.advanceStageInflight);

  const [releasedHere, setReleasedHere] = useState(false);
  const [failure, setFailure] = useState(null);
  const [handoffWarnings, setHandoffWarnings] = useState(null);
  const [running, setRunning] = useState(false);

  if (!isOpen || !caseInfo) return null;

  const busy = running || releaseInflight || advanceStageInflight;
  const released = releasedHere || !!caseInfo.released_at;

  function close() {
    if (busy) return;
    setReleasedHere(false);
    setFailure(null);
    setHandoffWarnings(null);
    onClose();
  }

  function done(message) {
    setReleasedHere(false);
    setFailure(null);
    setHandoffWarnings(null);
    toast.success(message);
    onClose();
  }

  async function run(acknowledge = false) {
    setFailure(null);
    setRunning(true);
    try {
      if (!released) {
        const r = await releaseCase();
        if (!r.ok && !r.alreadyReleased) { setFailure(r); return; }
        setReleasedHere(true);
        if (!handOff) { done(`Released to ${ownerName}`); return; }
      }
      const s = await advanceStage({ toStage: nextStage, notes: null, acknowledgeHandoff: acknowledge });
      if (s.ok) {
        if (s.warning) toast.warning(`Warning: ${s.warning.detail}`);
        done(`Released to ${ownerName} and handed off`);
        return;
      }
      if (s.handoffWarnings) { setHandoffWarnings(s.handoffWarnings); return; }
      setHandoffWarnings(null);
      if (s.conflict) {
        toast.error(`Stage advanced under you (now '${s.conflict.current_stage}'). Refreshing case…`);
        await loadCase(caseInfo.id);
        done(`Released to ${ownerName}`);
        return;
      }
      setFailure(s);
    } finally {
      setRunning(false);
    }
  }

  const title = handOff ? `Release to ${ownerName} and hand off?` : `Release to ${ownerName}?`;
  const confirmLabel = released && handOff
    ? "Retry hand-off"
    : handOff ? "Release and hand off" : "Release";

  return (
    <>
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && close()}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="w-full max-w-md bg-bg-soft border border-slate-700 rounded-xl shadow-2xl">
        <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-100 flex items-center gap-1.5">
            <PackageCheck size={12} className="text-sky-400" />
            {title}
          </h3>
          <button
            type="button"
            onClick={close}
            disabled={busy}
            className="text-fg-secondary hover:text-slate-200 transition disabled:opacity-40"
            title="Cancel"
            aria-label="Cancel"
          >
            <XIcon size={14} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {released && handOff ? (
            <p className="text-[11px] text-slate-300 leading-relaxed">
              Released to {ownerName}. The hand-off did not go through, so the case is
              still at its current stage: {ownerName} can open it but it is not yet theirs to work.
            </p>
          ) : (
            <p className="text-[11px] text-slate-300 leading-relaxed">
              {ownerName} will be able to open this case and its findings. This can't be undone.
              {handOff && nextStageLabel ? ` Then the case moves to ${nextStageLabel}.` : ""}
            </p>
          )}
          <ActionError failure={failure} />
        </div>

        <div className="px-5 py-3 border-t border-slate-800 flex justify-end gap-1.5">
          <button
            type="button"
            onClick={close}
            disabled={busy}
            className="text-[11px] px-3 py-1.5 rounded border border-slate-700 text-slate-300
                       hover:bg-slate-800 hover:border-slate-600 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => run(false)}
            disabled={busy}
            className="flex items-center gap-1 text-[11px] px-3 py-1.5 rounded border
                       bg-sky-500/15 border-sky-500/50 text-sky-200 hover:bg-sky-500/25
                       disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            {busy && <Loader2 size={11} className="animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
    <HandoffWarningsModal
      isOpen={handoffWarnings !== null}
      detail={handoffWarnings}
      action="forward"
      busy={busy}
      onConfirm={() => run(true)}
      onClose={() => setHandoffWarnings(null)}
    />
    </>
  );
}
